/**
 * WP-04 — client-side storage for vehicle-challenge sessions.
 *
 * A session is minted per DOCUMENT and is only valid for that document, so it
 * is stored per document link rather than as one ambient "logged in" value. The
 * shape of the storage mirrors the shape of the credential; a single global
 * slot would invite code that sends the wrong document's session and gets a
 * confusing 401.
 *
 * sessionStorage, not localStorage, and deliberately:
 *   - it is scoped to the tab, so a shared or kiosk browser does not carry an
 *     approval credential into the next person's session;
 *   - it is cleared when the tab closes, which matches the 30-minute server-side
 *     TTL rather than outliving it by weeks.
 *
 * The server is the authority on expiry regardless. Anything here is a cache of
 * a credential the server will re-validate on every request, so a stale or
 * tampered entry costs a 401 and a re-prompt, never access.
 */

const PREFIX = 'cnf.portalChallenge.';

/** Storage that degrades to a no-op where sessionStorage is unavailable. */
function store(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    // Safari private mode and some embedded webviews throw on access. The
    // challenge still works; the customer is just asked again on reload.
    return null;
  }
}

export function getChallengeToken(documentToken: string | undefined | null): string | null {
  if (!documentToken) return null;
  try {
    return store()?.getItem(PREFIX + documentToken) ?? null;
  } catch {
    return null;
  }
}

export function setChallengeToken(documentToken: string, challengeToken: string): void {
  try {
    store()?.setItem(PREFIX + documentToken, challengeToken);
  } catch {
    /* non-fatal: the customer is re-prompted on the next load */
  }
}

export function clearChallengeToken(documentToken: string | undefined | null): void {
  if (!documentToken) return;
  try {
    store()?.removeItem(PREFIX + documentToken);
  } catch {
    /* non-fatal */
  }
}

/**
 * Pull the document token out of a public document URL.
 *
 * Used by the `publicApi` interceptor so every call for a document carries that
 * document's challenge session automatically — including the ones added later
 * by someone who does not know this exists. Attaching it at the call site
 * instead would mean each new public route is one forgotten header away from
 * looking broken.
 *
 * Matches `/public/estimates/<token>` and `/public/work-orders/<token>`, with
 * or without a trailing sub-path.
 */
export function documentTokenFromUrl(url: string | undefined): string | null {
  if (!url) return null;
  const m = url.match(/\/public\/(?:estimates|work-orders)\/([^/?#]+)/);
  return m ? m[1] : null;
}
